import React, { forwardRef, useEffect, useRef } from "react";
import Select, { SelectProps, transformDataToSelectOption } from "@/components/atoms/Select";
import { useGetRegions } from "@/services/RegisterCompany";
import { useTranslation } from "react-i18next";
import { Region } from "@/types";
import { useAuth } from "@/contexts/AuthContext";

type keyOptions = Extract<keyof Region, string>;

interface SelectPropsExtended extends SelectProps {
  valueKey: keyOptions;
  labelKey: keyOptions;
  setSelectedRegion?: (region: Region) => void;
  defaultRegion?: string | null | undefined;
  countryIso: string;
}

const SelectRegion = forwardRef<HTMLInputElement, SelectPropsExtended>(
  (
    { valueKey, labelKey, setSelectedRegion, onChange, value, defaultRegion, countryIso, ...restProps },
    ref // 2. Receive the ref here
  ) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: regionOptions, isLoading } = useGetRegions(countryIso, {
      enabled: !!user?.language?.id,
    });
    const sourceData: Region[] = regionOptions || [];
    const options = transformDataToSelectOption(sourceData, valueKey || "id", labelKey || "name", t);

    const hasSetDefaultRef = useRef(false);

    useEffect(() => {
      if (!setSelectedRegion || hasSetDefaultRef.current || !sourceData.length) return;

      const val = value ?? defaultRegion;
      if (val === undefined || val === null || val === "") return;

      const selectedValue = valueKey === "id" ? Number(val) : val;
      const foundRegion = sourceData.find((c) => (c as any)[valueKey] === selectedValue);

      if (foundRegion) {
        setSelectedRegion(foundRegion);
        hasSetDefaultRef.current = true;
      }
    }, [sourceData, value, defaultRegion, valueKey, setSelectedRegion]);

    const handleOnChange = (val: string | null, option: any) => {
      onChange?.(val, option);

      if (setSelectedRegion && val != null && sourceData.length > 0) {
        const selectedValue = valueKey === "id" ? Number(val) : val;
        const foundRegion = sourceData.find((c) => (c as any)[valueKey] === selectedValue);
        if (foundRegion) setSelectedRegion(foundRegion);
      }
    };

    return (
      <Select
        ref={ref} // 3. Pass the ref down
        id="region-select"
        data={options}
        loading={isLoading}
        comboboxProps={{ withinPortal: true, zIndex: 10001 }}
        onChange={handleOnChange}
        value={value ?? undefined}
        defaultValue={defaultRegion ?? undefined}
        {...restProps}
      />
    );
  }
);

SelectRegion.displayName = "SelectRegion"; // Good practice when using forwardRef

export default SelectRegion;
